import { TiltedCard } from '@components/tiltedCard/TiltedCard'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFire } from '@fortawesome/sharp-duotone-solid-svg-icons'
import { SwayCashIcon } from '@icons/SwayCash'
import { Box, Button, Group, Stack, Text, useMantineTheme } from '@mantine/core'
import { getAmountOfSwayCashBonusEarned } from '@pages/leaderboards/utils/leaderboards.utils'
import { useScreenDimensionsStore } from '@stores/useScreenDimensionsStore'
import { formatSwayCash } from '@util/money'
import { BOLD, formatUnit, SEMI_BOLD } from '@util/utils'
import React from 'react'
import { ProtectYourStreakSection } from './ProtectYourStreakSection'

interface AnswerStreakInProgressModalContentProps {
  streak: number
  moreThanSevenDaysReward: number
  moreThanFourteenDaysReward: number
  onClose(): void
}

export function AnswerStreakInProgressModalContent({
  streak,
  moreThanSevenDaysReward,
  moreThanFourteenDaysReward,
  onClose,
}: AnswerStreakInProgressModalContentProps) {
  const theme = useMantineTheme()
  const { safeAreaInsets } = useScreenDimensionsStore()

  const amountOfSwayCashBonusEarned =
    getAmountOfSwayCashBonusEarned(
      streak,
      moreThanSevenDaysReward,
      moreThanFourteenDaysReward
    ) || 0
  const hasNotReached14DayStreak = streak < 14

  return (
    <Stack
      justify="center"
      align="center"
      gap={24}
      h="100%"
      pt={safeAreaInsets.top}
    >
      <Stack gap={0}>
        <FontAwesomeIcon
          icon={faFire}
          style={
            {
              color: 'inherit',
              height: 100,
              marginBottom: '5px',
              '--fa-primary-color': theme.colors.white,
              '--fa-secondary-color': theme.colors['deep-blue'][9],
              '--fa-primary-opacity': 1,
              '--fa-secondary-opacity': 1,
            } as React.CSSProperties
          }
        />
        <Text ta="center" fw={BOLD} size="64px" mt={5}>
          {streak}
        </Text>
        <Text ta="center" fw={BOLD} fz={28}>
          days in a row!
        </Text>
      </Stack>

      {hasNotReached14DayStreak ? (
        <Text ta="center" fw={BOLD}>
          You have {14 - streak} {formatUnit(14 - streak, 'day')} left until you
          hit a 14-day streak!
        </Text>
      ) : (
        <Text ta="center" fw={BOLD}>
          You earned
          <SwayCashIcon
            className="ml-1 inline-block h-4 w-4"
            allFilled
            color="white"
          />{' '}
          {streak > 14 ? '1.00' : '0.50'} as a bonus due to your streak! 🎉{' '}
          <br />
        </Text>
      )}

      <Group justify="center">
        <TiltedCard
          backgroundOne={theme.colors.orange[3]}
          ta="center"
          c="white"
          size="lg"
        >
          Your cumulative streak bonus: <br />
          <SwayCashIcon
            className="ml-1 inline-block h-4 w-4"
            allFilled
            color="white"
          />{' '}
          {formatSwayCash(amountOfSwayCashBonusEarned)}
        </TiltedCard>
      </Group>
      <ProtectYourStreakSection onClose={onClose} />
      <Text ta="center" fz={15} fw={SEMI_BOLD}>
        Come back tomorrow to keep your streak going!
      </Text>
      <Box w="90%" pos="absolute" bottom={safeAreaInsets.bottom + 10}>
        <Button
          onClick={onClose}
          fullWidth
          mih={40}
          color="swurple"
          radius="sm"
        >
          Awesome!
        </Button>
      </Box>
    </Stack>
  )
}
